import React, { useState, useRef, useCallback } from "react";
import { config } from "../config/environment";

interface FoodItem {
  id: string;
  food_name: string;
  portion_size: string;
  portion_amount: number;
  portion_unit: string;
  calories: number;
  protein: number;
  carbohydrates: number;
  fat: number;
  fiber: number;
}

interface DietModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  selectedDate: string;
}

const getDefaultFormData = (): FoodItem => ({
  id: '',
  food_name: "",
  portion_size: "1인분",
  portion_amount: 1,
  portion_unit: "그릇",
  calories: 0,
  protein: 0,
  carbohydrates: 0,
  fat: 0,
  fiber: 0,
});

export const DietModal: React.FC<DietModalProps> = React.memo(
  ({ isOpen, onClose, onSubmit, mealType, selectedDate }) => {
    const [formData, setFormData] = useState<FoodItem>(getDefaultFormData());
    const [error, setError] = useState<string>("");
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);
    const [inputMethod, setInputMethod] = useState<'manual' | 'image'>('manual');
    const [imagePreview, setImagePreview] = useState<string>("");
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dietLogId, setDietLogId] = useState<string>("");
    const [detectedFoods, setDetectedFoods] = useState<any[]>([]);

    // 식단 기록 생성 함수 - useCallback으로 메모이제이션
    const createDietLog = useCallback(async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) throw new Error('Authentication required');

        const response = await fetch(`${config.apiUrl}/diet/log`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            log_date: selectedDate,
            meal_type: mealType,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          setDietLogId(data.diet_log_id);
        }
      } catch (error) {
        console.error('Failed to create diet log:', error);
        setError('식단 기록을 생성할 수 없습니다');
      }
    }, [selectedDate, mealType]);

    // Hook은 반드시 조건 없이 호출되어야 함
    React.useEffect(() => {
      if (isOpen && !dietLogId) {
        createDietLog();
      }
    }, [isOpen, dietLogId, createDietLog]);

    // mealType이 변경되면 dietLogId 초기화 (새로운 식사 유형에 대한 새 기록 생성)
    React.useEffect(() => {
      if (isOpen) {
        setDietLogId(""); // 초기화하면 위의 useEffect가 다시 createDietLog 호출
      }
    }, [mealType, isOpen]);

    if (!isOpen) return null;

    const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: isNaN(Number(value)) ? value : Number(value),
      }));
      setError("");
    };

    // 이미지를 효율적으로 압축하는 함수
    const compressImage = (base64: string): Promise<string> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;

            // 최대 크기 600x600으로 리사이징
            const maxSize = 600;
            if (width > height) {
              if (width > maxSize) {
                height = Math.round((height * maxSize) / width);
                width = maxSize;
              }
            } else {
              if (height > maxSize) {
                width = Math.round((width * maxSize) / height);
                height = maxSize;
              }
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            
            if (ctx) {
              ctx.fillStyle = '#ffffff'; // 배경 색상
              ctx.fillRect(0, 0, width, height);
              ctx.drawImage(img, 0, 0, width, height);
              
              // JPEG 품질 0.5 (50%)로 강력하게 압축
              const compressedBase64 = canvas.toDataURL('image/jpeg', 0.5);
              resolve(compressedBase64);
            } else {
              reject(new Error('Canvas context를 가져올 수 없습니다'));
            }
          } catch (error) {
            reject(error);
          }
        };
        
        img.onerror = () => reject(new Error('이미지 로드 실패'));
        img.src = base64;
      });
    };

    const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        // 파일 크기 확인 (50MB 이상이면 거절)
        if (file.size > 50 * 1024 * 1024) {
          setError('이미지 크기가 너무 큽니다. 50MB 이하의 이미지를 선택해주세요.');
          return;
        }

        setError(""); // 이전 오류 메시지 제거
        setIsAnalyzing(true); // 압축 중 로딩 표시

        const reader = new FileReader();
        reader.onerror = () => {
          setError('파일 읽기 실패');
          setIsAnalyzing(false);
        };
        
        reader.onload = async (event) => {
          try {
            const originalBase64 = event.target?.result as string;
            
            // 이미지 압축
            const compressedBase64 = await compressImage(originalBase64);
            
            // 압축된 크기 계산
            const compressedSizeKB = Math.round(((compressedBase64.length - 22) * 0.75) / 1024);
            
            setImagePreview(compressedBase64);
            setSuccessMessage(`✓ 이미지 압축 완료 (${compressedSizeKB}KB)`);
            
            // 3초 후 메시지 제거
            setTimeout(() => setSuccessMessage(""), 3000);
          } catch (error) {
            setError(`이미지 처리 중 오류가 발생했습니다: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
            console.error('Image compression error:', error);
          } finally {
            setIsAnalyzing(false);
          }
        };
        reader.readAsDataURL(file);
      }
    };

    const handleAIAnalyze = async () => {
      if (!imagePreview) {
        setError('이미지를 선택해주세요');
        return;
      }

      // diet_log_id가 없으면 먼저 생성
      let logId = dietLogId;
      if (!logId) {
        try {
          const token = localStorage.getItem('authToken');
          if (!token) throw new Error('Authentication required');

          const createResponse = await fetch(`${config.apiUrl}/diet/log`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              log_date: selectedDate,
              meal_type: mealType,
            }),
          });

          if (createResponse.ok) {
            const createData = await createResponse.json();
            logId = createData.diet_log_id;
            setDietLogId(logId);
          } else {
            throw new Error('식단 기록 생성 실패');
          }
        } catch (createError) {
          setError('식단 기록을 생성할 수 없습니다');
          return;
        }
      }

      setIsAnalyzing(true);
      setError("");

      try {
        const token = localStorage.getItem('authToken');
        if (!token) throw new Error('Authentication required');

        // Base64 문자열에서 프리픽스 제거
        const base64Data = imagePreview.split(',')[1] || imagePreview;

        // 컨트롤러 생성 (최대 120초 타임아웃 - AI 분석은 시간이 걸릴 수 있음)
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 120000);

        const response = await fetch(`${config.apiUrl}/diet/ai/analyze`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            image_base64: base64Data,
            diet_log_id: logId,
          }),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.foods && data.foods.length > 0) {
            // 백엔드에서 자동으로 저장됨
            setSuccessMessage(`✅ ${data.message || `${data.foods.length}개 음식이 자동으로 저장되었습니다!`}`);
            
            // 섭취한 음식 정보 표시
            const foodDetails = data.foods
              .map((f: any) => `${f.food_name} (${f.calories}kcal)`)
              .join(', ');
            console.log('자동 저장된 음식:', foodDetails);
            
            // 2초 후 모달 닫고 페이지 새로고침 (폼 초기화)
            setTimeout(() => {
              setFormData(getDefaultFormData());
              setImagePreview("");
              setDetectedFoods([]);
              setSuccessMessage("");
              setIsAnalyzing(false); // 2초 후 로딩 상태 해제
              onClose();
              onSubmit(); // 부모 컴포넌트에서 데이터 재로드
            }, 2000);
            
            return;
          } else {
            setError('음식을 인식하지 못했습니다. 다시 시도해주세요.');
            setIsAnalyzing(false);
          }
        } else if (response.status === 413) {
          setError('이미지가 너무 큽니다. 더 작은 이미지를 선택해주세요.');
          setIsAnalyzing(false);
        } else if (response.status === 500) {
          // 에러 응답에서 메시지 추출
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.error || 'AI 분석 중 오류가 발생했습니다';
          
          // OpenAI 텍스트 응답 에러인 경우
          if (errorMessage.includes('non-JSON') || errorMessage.includes('returned text')) {
            setError('⚠️ 이미지에서 음식을 인식할 수 없습니다.\n다른 이미지를 시도하거나 아래에서 수동으로 입력해주세요.');
          } else {
            setError(`서버 오류: ${errorMessage || 'OpenAI API 설정을 확인해주세요.'}`);
          }
          setIsAnalyzing(false);
        } else {
          setError(`음식 인식에 실패했습니다 (${response.status})`);
          setIsAnalyzing(false);
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          setError('요청 시간 초과(120초). 요청이 너무 오래 걸렸습니다. 다시 시도해주세요.');
        } else {
          console.error('AI Analysis failed:', error);
          setError('음식 인식 중 오류가 발생했습니다: ' + ((error as Error).message || '알 수 없는 오류'));
        }
        setIsAnalyzing(false);
      }
    };

    const handleAddDetectedFood = (food: any) => {
      setFormData({
        id: '',
        food_name: food.name,
        portion_size: food.portion,
        portion_amount: 1,
        portion_unit: "개",
        calories: food.calories || 0,
        protein: food.protein || 0,
        carbohydrates: food.carbohydrates || 0,
        fat: food.fat || 0,
        fiber: food.fiber || 0,
      });
      setDetectedFoods([]);
    };

    const validateForm = (): boolean => {
      if (!formData.food_name.trim()) {
        setError("음식명을 입력해주세요");
        return false;
      }
      return true;
    };

    const handleSubmit = async () => {
      if (!validateForm()) return;
      if (!dietLogId) {
        setError('식단 기록이 초기화되지 않았습니다');
        return;
      }

      setIsLoading(true);
      setError("");

      try {
        const token = localStorage.getItem('authToken');
        if (!token) throw new Error('Authentication required');

        const response = await fetch(`${config.apiUrl}/diet/item`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            diet_log_id: dietLogId,
            food_name: formData.food_name,
            portion_size: formData.portion_size,
            portion_amount: formData.portion_amount,
            portion_unit: formData.portion_unit,
            calories: formData.calories,
            protein: formData.protein,
            carbohydrates: formData.carbohydrates,
            fat: formData.fat,
            fiber: formData.fiber,
            input_method: inputMethod === 'image' ? 'ai_image' : 'manual',
            original_image_url: inputMethod === 'image' ? imagePreview : null,
          }),
        });

        if (response.ok) {
          await response.json();
          setSuccessMessage('식단 항목이 추가되었습니다');
          setFormData(getDefaultFormData());
          setImagePreview("");
          setDetectedFoods([]);
          
          setTimeout(async () => {
            await onSubmit();
            onClose();
          }, 500);
        } else {
          setError('식단 항목 추가에 실패했습니다');
        }
      } catch (error) {
        console.error('Failed to add food item:', error);
        setError((error as Error).message || '오류가 발생했습니다');
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end z-50 animate-fadeIn">
        <div className="w-full bg-white rounded-t-3xl p-4 max-h-[90vh] overflow-y-auto animate-slideUp">
          {/* 헤더 */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-[16px] font-bold text-slate-900">식단 기록</h2>
            <button
              onClick={onClose}
              className="text-[24px] text-slate-400 hover:text-slate-600"
            >
              ✕
            </button>
          </div>

          {/* 입력 방식 선택 */}
          <div className="flex space-x-2 mb-4">
            <button
              onClick={() => setInputMethod('manual')}
              className={`flex-1 py-2 rounded-lg text-[13px] font-semibold transition-all ${
                inputMethod === 'manual'
                  ? 'bg-blue-500 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              직접입력
            </button>
            <button
              onClick={() => setInputMethod('image')}
              className={`flex-1 py-2 rounded-lg text-[13px] font-semibold transition-all ${
                inputMethod === 'image'
                  ? 'bg-blue-500 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              사진분석
            </button>
          </div>

          {/* 이미지 입력 모드 */}
          {inputMethod === 'image' && (
            <div className="mb-4 pb-4 border-b border-slate-200">
              <div className="relative mb-3">
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="preview"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => setImagePreview("")}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-48 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <span className="text-[32px] mb-2">📷</span>
                    <span className="text-[12px] text-slate-600">사진을 선택하세요</span>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </div>

              {imagePreview && (
                <button
                  onClick={handleAIAnalyze}
                  disabled={isAnalyzing}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 rounded-lg font-semibold text-[13px] hover:shadow-lg transition-all disabled:opacity-50"
                >
                  {isAnalyzing ? '분석 중...' : 'AI로 음식 인식하기'}
                </button>
              )}

              {/* 인식된 음식 목록 */}
              {detectedFoods.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-[12px] font-semibold text-slate-900">인식된 음식:</p>
                  {detectedFoods.map((food, idx) => (
                    <div
                      key={idx}
                      className="flex justify-between items-center p-2 bg-blue-50 rounded-lg border border-blue-200 cursor-pointer hover:bg-blue-100"
                      onClick={() => handleAddDetectedFood(food)}
                    >
                      <div>
                        <p className="text-[12px] font-semibold text-slate-900">{food.name}</p>
                        <p className="text-[11px] text-slate-600">{food.portion}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[12px] font-semibold text-slate-900">{food.calories}</p>
                        <p className="text-[10px] text-slate-500">kcal</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 직접입력 모드만 폼 필드 표시 */}
          {inputMethod === 'manual' && (
          <div className="space-y-3">
            {/* 음식명 */}
            <div>
              <label className="text-[12px] font-semibold text-slate-700 block mb-1">음식명 *</label>
              <input
                type="text"
                name="food_name"
                value={formData.food_name}
                onChange={handleChange}
                placeholder="예: 밥, 계란말이"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* 섭취량 */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[12px] font-semibold text-slate-700 block mb-1">양</label>
                <input
                  type="number"
                  name="portion_amount"
                  value={formData.portion_amount}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-slate-700 block mb-1">단위</label>
                <select
                  name="portion_unit"
                  value={formData.portion_unit}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                >
                  <option value="그릇">그릇</option>
                  <option value="개">개</option>
                  <option value="장">장</option>
                  <option value="잔">잔</option>
                  <option value="스푼">스푼</option>
                  <option value="g">g</option>
                  <option value="ml">ml</option>
                </select>
              </div>
            </div>

            {/* 칼로리 */}
            <div>
              <label className="text-[12px] font-semibold text-slate-700 block mb-1">칼로리 (kcal)</label>
              <input
                type="number"
                name="calories"
                value={formData.calories}
                onChange={handleChange}
                step="1"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
              />
            </div>

            {/* 영양소 */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[12px] font-semibold text-slate-700 block mb-1">단백질 (g)</label>
                <input
                  type="number"
                  name="protein"
                  value={formData.protein}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-slate-700 block mb-1">탄수화물 (g)</label>
                <input
                  type="number"
                  name="carbohydrates"
                  value={formData.carbohydrates}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-slate-700 block mb-1">지방 (g)</label>
                <input
                  type="number"
                  name="fat"
                  value={formData.fat}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-slate-700 block mb-1">식이섬유 (g)</label>
                <input
                  type="number"
                  name="fiber"
                  value={formData.fiber}
                  onChange={handleChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                />
              </div>
            </div>
          </div>
          )}

          {/* 오류/성공 메시지 */}
          {error && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-[12px] text-red-700">{error}</p>
            </div>
          )}

          {successMessage && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-[12px] text-green-700">{successMessage}</p>
            </div>
          )}

          {/* 버튼 - 입력 방식에 따라 다르게 표시 */}
          <div className="flex space-x-2 mt-4">
            <button
              onClick={onClose}
              className="flex-1 bg-slate-200 text-slate-700 py-2 rounded-lg font-semibold text-[13px] hover:bg-slate-300"
            >
              취소
            </button>
            {inputMethod === 'manual' && (
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="flex-1 bg-blue-500 text-white py-2 rounded-lg font-semibold text-[13px] hover:bg-blue-600 disabled:opacity-50"
              >
                {isLoading ? '저장 중...' : '저장'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
);

DietModal.displayName = 'DietModal';
